import { inject, Injectable, OnDestroy } from '@angular/core';
import {
  ActiveConversationStore,
  ConversationStore,
  IdentityStore,
  MessageStore,
  ParticipantWithSourceId,
  TenantStore,
  UserStore,
} from '../stores';
import {
  Conversation,
  ConversationIncludeOption,
  ConversationLockRequest,
  Location,
  MessageKind,
  Participant,
  ParticipantKind,
  WsConversationArchived,
  WsConversationReadStatus,
  WsConversationUpdated,
  WsMessageUpdated,
  WsNewMessage,
  WsParticipantCreated,
  WsParticipantHadConversationOpen,
  WsParticipantReadStatus,
  WsParticipantWasTyping,
} from '@clientwire/api';
import { addEntities, setAllEntities, updateEntity } from '@ngrx/signals/entities';
import { MessagesWithConversationId } from '../interfaces';
import { FormControl, Validators } from '@angular/forms';
import { updateState } from '@angular-architects/ngrx-toolkit';
import { WireApiService } from './wire-api.service';
import { MessageStoreHelperService } from './message-store-helper.service';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { logger } from '../utils';
import { FilesToUpload } from '@akostm/orbit-ui';
import * as uuid from 'uuid';
import { Overlay } from '@angular/cdk/overlay';
import { Dialog } from '@angular/cdk/dialog';
import { LocationOverlayComponent } from '../atomic-design/templates';
import { WindowService } from './window.service';

@Injectable({
  providedIn: 'root',
})
export class ConversationHelperService implements OnDestroy {
  readonly #activeConversationStore = inject(ActiveConversationStore);
  readonly #conversationStore = inject(ConversationStore);
  readonly #messageStore = inject(MessageStore);
  readonly #tenantStore = inject(TenantStore);
  readonly #userStore = inject(UserStore);
  readonly #identityStore = inject(IdentityStore);
  readonly #wireApiService = inject(WireApiService);
  readonly #messageStoreHelperService = inject(MessageStoreHelperService);
  readonly #translateService = inject(TranslateService);
  readonly #overlay = inject(Overlay);
  readonly #dialog = inject(Dialog);
  readonly #windowService = inject(WindowService);
  #unreadMessagesSubscription: Subscription | undefined;
  private connectedConversations: {
    conversationId: string;
    unsubscribe: () => void | undefined;
  }[] = [];

  #conversationsUpdateUnsubscribe: (() => void) | null = null;

  //#region Lifecycle

  ngOnDestroy(): void {
    this.connectedConversations.forEach((element) => {
      element.unsubscribe();
    });
    this.#unreadMessagesSubscription?.unsubscribe();
  }

  //#endregion

  //#region Conversations

  addToActiveConversations(conversations: Conversation[]) {
    // Add conversation to the active conversation store
    updateState(
      this.#activeConversationStore,
      'Add conversation to ActiveConversationStore',
      addEntities(
        conversations.map((c) => {
          const files: FilesToUpload[] = [];
          const messageSourceId = uuid.v4();
          const messageInput = new FormControl('', [Validators.maxLength(4096)]);
          return { ...c, files, messageSending: false, messageInput, messageSourceId };
        })
      )
    );

    // when the new conversation is the onlyone, make it in focus
    if (this.#activeConversationStore.entities().length === 1 && conversations[0]) {
      this.#activeConversationStore.setConversationIntoFocus(conversations[0].id);
    }

    // Add conversation to the message store
    const conversationIds = conversations.map((c) => c.id);
    const newConversations: MessagesWithConversationId[] = conversationIds.map((conversationId) => {
      return {
        id: conversationId,
        messages: [],
        messageIdsWithError: [],
        lastUpdateEvent: 'init',
        messageAppendLoading: false,
        messagePrependLoading: false,
        messagesLimit: this.#windowService.messagesLimit(),
      };
    });
    updateState(
      this.#messageStore,
      'Add conversations to MessageStore',
      addEntities(newConversations)
    );

    // Store the opened constersations state
    this.#activeConversationStore.storeActiveConversations();
    // Subscribe to websocket for the new conversations
    conversations.forEach((conversation) => {
      this.subscribeToConversation(conversation.id);
    });
  }

  async restoreActiveConversations() {
    // Restore the opened conversation ids from local storage
    const activeConversations = this.#activeConversationStore.getStoredConversationIds();
    if (
      !activeConversations ||
      (Array.isArray(activeConversations) && activeConversations.length === 0)
    )
      return;

    const conversationsByIds = await this.getConversationByIds(activeConversations);

    conversationsByIds.conversations?.forEach((conversation) => {
      if (conversation) {
        this.addToActiveConversations([conversation]);
      }
    });
  }

  async getConversationByIds(conversationIds: string[]) {
    return this.#wireApiService.conversationsApi.getConversationsByIds({
      conversationsByIdsRequest: { ids: conversationIds },
      includes: [
        ConversationIncludeOption.Participants,
        ConversationIncludeOption.ConversationType,
        ConversationIncludeOption.ListItemTemplateData,
        ConversationIncludeOption.ClientParticipantFrontendUrl,
      ],
    });
  }

  async addToConversationListByIds(conversationIds: string[]) {
    const conversationsByIds = await this.getConversationByIds(conversationIds);

    if (!conversationsByIds.conversations) return;
    // update/add conversation to the conversation store
    updateState(
      this.#conversationStore,
      'Add conversations to ConversationStore',
      setAllEntities([...conversationsByIds.conversations, ...this.#conversationStore.entities()])
    );
  }

  async archiveAndCloseConversation(conversationId: string) {
    this.#conversationStore.archiveConversation(conversationId);
    this.#activeConversationStore.removeConversation(conversationId);
  }

  //#endregion

  //#region Messages

  async createNewMessage(
    conversationId: string,
    kind: MessageKind,
    messageSourceId?: string,
    text?: string,
    coordinates?: Location,
    formattedAddress?: string,
    imageSrc?: string
  ) {
    const tenantId = this.#tenantStore.id();
    if (!tenantId || !conversationId) {
      // TODO: if the logged in role is a client participant then we would need to check for the participantId
      logger.error('Missing tenantId, conversationId or participantId');
      return;
    }
    const conversation = this.#activeConversationStore.entityMap()[conversationId];
    if (!conversation)
      throw new Error(
        this.#translateService.instant('SHARED.SERVICES.CONVERSATION.NOT_FOUND_ERROR')
      );

    const participant =
      this.#identityStore.identityType() === 'user'
        ? this.#findParticipantInConversationByUserId(conversation, this.#userStore.id())
        : this.#findParticipantInConversationByParticipantId(
            conversation,
            this.#identityStore.clientParticipantId()
          );

    if (!participant) {
      throw new Error(
        this.#translateService.instant('SHARED.SERVICES.CONVERSATION.PARTICIPANT_ID_MISSING')
      );
    }

    const oldMessages = this.#messageStore.entityMap()[conversationId]?.messages ?? [];
    const lastSequence = oldMessages.length ? oldMessages[0]?.sequence : 1;
    const nextSequence = lastSequence ? lastSequence + 1 : 1;
    const localMessage = this.#messageStoreHelperService.createNewMessage(
      tenantId,
      conversationId,
      participant?.id ||
        this.#translateService.instant('SHARED.SERVICES.CONVERSATION.PARTICIPANT_ID_MISSING'),
      nextSequence,
      kind,
      messageSourceId,
      text,
      coordinates,
      formattedAddress,
      imageSrc
    );

    this.#messageStore.addMessages(
      conversationId,
      [localMessage],
      'prepend',
      true,
      undefined,
      'localMessage'
    );
    this.#activeConversationStore.setNewMessageSourceId(conversationId);

    return await this.#messageStore.sendLocalMessageToServer(localMessage);
  }

  async loadMoreMessages(
    messageEntity: MessagesWithConversationId | undefined,
    position: 'prepend' | 'append' = 'append',
    limit = 10,
    fetchUntilHasMoreMessages = false
  ): Promise<void> {
    if (!messageEntity)
      throw new Error(this.#translateService.instant('SHARED.PAGES.DASHBOARD.NO_CONVERSATION'));

    const lastIndex = position === 'prepend' ? 0 : -1;
    const lastMessage = messageEntity.messages.slice(lastIndex)[0];
    if (
      !lastMessage ||
      (position === 'prepend' && messageEntity.messageAppendLoading === true) ||
      (position === 'append' && messageEntity.messagePrependLoading === true)
    ) {
      return;
    }

    const last_sequence = lastMessage.sequence;
    if (last_sequence) {
      await this.#messageStore.getMessagesOfConversation(
        messageEntity.id,
        limit,
        last_sequence,
        position,
        false,
        position === 'prepend' ? 'ASC' : 'DESC',
        fetchUntilHasMoreMessages
      );
    }
  }

  //#endregion

  //#region Participants

  #updateConversationParticipants(participant: Participant) {
    const conversation = this.#activeConversationStore
      .entities()
      .find((c) => c.id === participant.conversationId);
    if (!conversation) return;
    const oldParticipants = conversation?.participants;
    const newParticipants = oldParticipants ? [...oldParticipants, participant] : [participant];

    this.#activeConversationStore.updateConversation(conversation.id, {
      participants: newParticipants,
    });
    this.#conversationStore.updateConversation(conversation.id, {
      participants: newParticipants,
    });
  }

  #findParticipantInConversationByUserId(
    conversation: Conversation,
    userId: string
  ): Participant | undefined {
    if (conversation?.participants) {
      return conversation.participants.find((participant) => participant.userId === userId);
    }
    return undefined;
  }

  #findParticipantInConversationByParticipantId(
    conversation: Conversation,
    participantId: string | null
  ): Participant | undefined {
    if (conversation?.participants && participantId) {
      return conversation.participants.find((participant) => participant.id === participantId);
    }
    return undefined;
  }

  async addAndUpdateParticipantToConversation(
    conversationId: string,
    userId: string
  ): Promise<Participant> {
    const newParticipant = await this.#addUserAsParticipant(conversationId, userId);
    this.#updateConversationParticipants(newParticipant);
    return newParticipant;
  }

  async #addUserAsParticipant(conversationId: string, userId: string): Promise<Participant> {
    const firstName = this.#userStore.firstname ? this.#userStore.firstname() : null;
    const lastName = this.#userStore.lastname ? this.#userStore.lastname() : null;
    let displayName = null;
    if (firstName && lastName) {
      displayName = `${firstName} ${lastName}`;
    } else if (firstName) {
      displayName = firstName;
    } else if (lastName) {
      displayName = lastName;
    }

    return await this.#activeConversationStore.addParticipantToConversation(conversationId, {
      id: userId,
      kind: ParticipantKind.User,
      userId: userId,
      displayName,
    });
  }

  updateConversationReadStatus(payload: WsConversationReadStatus) {
    this.#activeConversationStore.updateConversation(payload.conversationId, {
      readStatus: {
        lastMessageSeen: payload.lastMessageSequenceSeen,
        unreadMessageCount: payload.unreadMessagesCount ?? 0,
      },
    });
    this.#conversationStore.updateConversation(payload.conversationId, {
      readStatus: {
        lastMessageSeen: payload.lastMessageSequenceSeen,
        unreadMessageCount: payload.unreadMessagesCount ?? 0,
      },
    });
  }

  updateConversationArchived(conversationId: string, archived: boolean) {
    this.#activeConversationStore.updateConversation(conversationId, {
      archived: archived,
    });
    this.#conversationStore.updateConversation(conversationId, {
      archived: archived,
    });

    if (archived) {
      if (this.#conversationStore.showArchiveOnly()) {
        const conversation = this.#activeConversationStore.entityMap()[conversationId];
        if (conversation) {
          this.#conversationStore.addConversation(conversation);
        }
      } else {
        this.#conversationStore.removeConversation(conversationId);
      }
    } else {
      if (this.#conversationStore.showArchiveOnly()) {
        this.#conversationStore.removeConversation(conversationId);
      } else {
        const conversation = this.#activeConversationStore.entityMap()[conversationId];
        if (conversation) {
          this.#conversationStore.addConversation(conversation);
        }
      }
    }
  }

  updateConversation(payload: WsConversationUpdated) {
    this.#activeConversationStore.updateConversation(payload.conversation.id, {
      participants: payload.conversation.participants,
      lockNote: payload.conversation.lockNote,
      lockExpiresAt: payload.conversation.lockExpiresAt,
      conversationData: payload.conversation.conversationData,
      labels: payload.conversation.labels,
    });

    this.#conversationStore.updateConversation(payload.conversation.id, {
      participants: payload.conversation.participants,
      lockNote: payload.conversation.lockNote,
      lockExpiresAt: payload.conversation.lockExpiresAt,
      conversationData: payload.conversation.conversationData,
      labels: payload.conversation.labels,
    });
  }

  //#endregion

  //#region Websocket Subscriptions
  // TODO: I am not sure this belongs in here, probably we need to refactor
  // the whole class, but at least its close to the conversations.
  // Subscribing to mesasge read updates most definitely does not belong here.
  // But its work in progress anyway so I keep it here.s
  subscribeToConversation(conversationId: string) {
    const tenantId = this.#tenantStore.id();
    if (!tenantId) return;
    logger.debug('Subscribing to conversation: ' + conversationId);

    // Check if the previous subscription already exists then unsubscribe first
    if (this.connectedConversations.find((element) => element.conversationId === conversationId)) {
      this.unsubscribeFromConversation(conversationId);
    }

    const unsubscribe = this.#wireApiService.subscribeToConversation(
      conversationId,
      async (data) => {
        switch (data.type) {
          case 'NEW_MESSAGE': {
            logger.debug('Received new NEW_MESSAGE:', data);
            await this.#messageStore.addMessages(
              conversationId,
              [(data as WsNewMessage).message],
              'prepend',
              true,
              undefined,
              'socketMessage'
            );
            break;
          }
          case 'MESSAGE_UPDATED': {
            logger.debug('Received new MESSAGE_UPDATED:', data);
            const message = (data as WsMessageUpdated).message;
            this.#messageStore.replaceMessage(conversationId, message, 'socketMessage');
            break;
          }
          case 'PARTICIPANT_READ_STATUS': {
            logger.debug('Received new PARTICIPANT_READ_STATUS:', data);
            const typedData = data as WsParticipantReadStatus;
            const { type, participantId, ...participantData } = typedData;
            const participant = {
              id: participantId,
              ...participantData,
            };
            this.#updateParticipantFieldsInConversation(participant);
            break;
          }
          case 'PARTICIPANT_HAD_CONVERSATION_OPEN': {
            logger.debug('Received new PARTICIPANT_HAD_CONVERSATION_OPEN:', data);
            const typedData = data as WsParticipantHadConversationOpen;
            const formattedData: Partial<Participant> = {
              id: typedData.participantId,
              conversationId: typedData.conversationId,
              hadConversationOpenAt: typedData.hadConversationOpenAt,
            };
            this.#updateParticipantFieldsInConversation(formattedData);
            break;
          }
          case 'PARTICIPANT_WAS_TYPING': {
            logger.debug('Received new PARTICIPANT_WAS_TYPING:', data);
            const typedData = data as WsParticipantWasTyping;
            const formattedData: Partial<ParticipantWithSourceId> = {
              id: typedData.participantId,
              conversationId: typedData.conversationId,
              wasTypingAt: typedData.wasTypingAt,
              sourceId: typedData.sourceId,
            };
            this.#updateParticipantFieldsInConversation(formattedData);
            break;
          }
          case 'CONVERSATION_ARCHIVED': {
            logger.debug('Received new CONVERSATION_ARCHIVED:', data);
            const wsConversationArchived = data as WsConversationArchived;
            this.updateConversationArchived(
              wsConversationArchived.conversationId,
              wsConversationArchived.archived
            );
            break;
          }
          case 'PARTICIPANT_CREATED': {
            logger.debug('Received new PARTICIPANT_CREATED:', data);
            const wsParticipantCreated = data as WsParticipantCreated;

            this.#activeConversationStore.updateConversation(conversationId, {
              participants: wsParticipantCreated.participants,
            });
            break;
          }
          default: {
            logger.debug('unhandled event: ', data);
          }
        }
      }
    );

    logger.debug('Subscribed to conversation: ' + conversationId);
    this.connectedConversations.push({ conversationId, unsubscribe });
  }

  subscribeToConversationParticipantsUpdated(conversationId: string) {
    this.#wireApiService.subscribeToConversationParticipantsUpdated(async (data) => {
      switch (data.type) {
        case 'PARTICIPANT_CREATED': {
          logger.debug('Received new PARTICIPANT_CREATED:', data);
          const wsParticipantCreated = data as WsParticipantCreated;

          this.#activeConversationStore.updateConversation(conversationId, {
            participants: wsParticipantCreated.participants,
          });
          break;
        }
        default: {
          logger.debug('unhandled event: ', data);
        }
      }
    });
  }

  #updateParticipantFieldsInConversation(newParticipantFields: Partial<ParticipantWithSourceId>) {
    if (!newParticipantFields.conversationId) return;
    const conversation =
      this.#activeConversationStore.entityMap()[newParticipantFields.conversationId];
    if (!conversation) return;
    const oldParticipants = conversation?.participants;
    if (!oldParticipants) {
      logger.error('No participants found in the conversation');
      return;
    }
    const oldParticipant = oldParticipants?.find((p) => p.id === newParticipantFields.id);
    if (!oldParticipant) {
      logger.error('The participant does not found in the conversation participants list');
      return;
    }
    const newParticipant = { ...oldParticipant, ...newParticipantFields };
    const newParticipants = oldParticipants.map((p) =>
      p.id === newParticipantFields.id ? newParticipant : p
    );

    this.#activeConversationStore.updateConversation(conversation.id, {
      participants: newParticipants,
    });
    this.#conversationStore.updateConversation(conversation.id, {
      participants: newParticipants,
    });
  }

  subscribeToUnreadMessages() {
    logger.debug('TODO: Subscribing to unread messages');
    // TODO: Implement this when the socket is ready.
    // const participantId = this.#userStore.participantId();
    // if (!participantId) return;
    // TODO:
    // const unreadUrl = `/topic/user/${email}/conversations_read_status`;
    // this.#unreadMessagesSubscription = this.#rxStompService
    //   .watch(unreadUrl)
    //   .subscribe((message: Message) => {
    //     const messageBody = JSON.parse(message.body);
    //     const inFocusConversationId = this.#activeConversationStore.inFocusConversationId();
    //     const scrolledToBottom =
    //       this.#activeConversationStore.entityMap()[messageBody.conversationPublicId]
    //         ?.scrolledToBottom;
    //     if (
    //       messageBody.unreadMessageCount === 0 ||
    //       (messageBody.conversationPublicId &&
    //         (inFocusConversationId !== messageBody.conversationPublicId || !scrolledToBottom))
    //     ) {
    //       const conversation =
    //         this.#conversationStore.entityMap()[messageBody.conversationPublicId];
    //       if (conversation) {
    //         const conversationData = {
    //           ...conversation.conversation_data,
    //           count: messageBody.unreadMessageCount,
    //         };
    //         this.#conversationStore.updateConversation(conversation, {
    //           conversation_data: conversationData,
    //           new_message_count: messageBody.unreadMessageCount,
    //         });
    //       }
    //       // Also update the active conversation as well.
    //       const activeConversation =
    //         this.#activeConversationStore.entityMap()[messageBody.conversationPublicId];
    //       if (activeConversation) {
    //         const conversationData = {
    //           ...activeConversation.conversation_data,
    //           count: messageBody.unreadMessageCount,
    //         };
    //         this.#activeConversationStore.updateConversation(activeConversation.id, {
    //           conversation_data: conversationData,
    //           new_message_count: messageBody.unreadMessageCount,
    //         });
    //       }
    //     }
    //   });
  }

  subscribeToConversationsUpdated() {
    this.#conversationsUpdateUnsubscribe = this.#wireApiService.subscribeToConversationUpdates(
      (payload) => {
        this.updateConversation(payload);
      }
    );
  }

  unsubscribeFromConversation(conversationId: string) {
    const connection = this.connectedConversations.find(
      (element) => element.conversationId === conversationId
    );
    connection?.unsubscribe();
    this.#removeConnectionFromArray(conversationId);
  }

  unsubscribeFromConversationsUpdated() {
    if (this.#conversationsUpdateUnsubscribe) {
      this.#conversationsUpdateUnsubscribe();
    }
  }

  #removeConnectionFromArray(conversationId: string) {
    const index = this.connectedConversations.findIndex(
      (element) => element.conversationId === conversationId
    );
    if (index > -1) {
      this.connectedConversations.splice(index);
    }
  }

  //#endregion

  async createConversationLabels(conversationId: string, label: string) {
    try {
      const response = await this.#wireApiService.conversationLabelsApi.updateConversationLabels({
        conversationId: conversationId,
        conversationLabelPutRequest: {
          labels: [label],
        },
        includes: [
          ConversationIncludeOption.Participants,
          ConversationIncludeOption.ConversationType,
          ConversationIncludeOption.ListItemTemplateData,
          ConversationIncludeOption.ClientParticipantFrontendUrl,
        ],
      });

      updateState(
        this.#conversationStore,
        'Update conversation in ConversationStore',
        updateEntity({ id: conversationId, changes: { ...response } })
      );
      updateState(
        this.#activeConversationStore,
        'Update conversation in ActiveConversationStore',
        updateEntity({ id: conversationId, changes: { ...response } })
      );
    } catch (err) {
      const errorMessage = (err as any)?.error?.message;
      if (errorMessage) {
        updateState(this.#conversationStore, 'Set error in ConversationStore', {
          error: errorMessage,
        });
        updateState(this.#activeConversationStore, 'Set error in ActiveConversationStore', {
          error: errorMessage,
        });
      } else {
        logger.error(err);
      }
    }
  }

  async deleteConversationLabel(conversationId: string, label: string) {
    try {
      const response = await this.#wireApiService.conversationLabelsApi.deleteConversationLabel({
        conversationId: conversationId,
        label: label,
        includes: [
          ConversationIncludeOption.Participants,
          ConversationIncludeOption.ConversationType,
          ConversationIncludeOption.ListItemTemplateData,
          ConversationIncludeOption.ClientParticipantFrontendUrl,
        ],
      });
      updateState(
        this.#conversationStore,
        'Update conversation in ConversationStore',
        updateEntity({ id: conversationId, changes: { ...response } })
      );
      updateState(
        this.#activeConversationStore,
        'Update conversation in ActiveConversationStore',
        updateEntity({ id: conversationId, changes: { ...response } })
      );
    } catch (err) {
      const errorMessage = (err as any)?.error?.message;
      if (errorMessage) {
        updateState(this.#conversationStore, 'Set error in ConversationStore', {
          error: errorMessage,
        });
        updateState(this.#activeConversationStore, 'Set error in ActiveConversationStore', {
          error: errorMessage,
        });
      } else {
        logger.error(err);
      }
    }
  }

  async lockConversation(conversationId: string, lockConversationRequest: ConversationLockRequest) {
    try {
      const response = await this.#wireApiService.conversationsApi.lockConversation({
        conversationId: conversationId,
        conversationLockRequest: lockConversationRequest,
        includes: [
          ConversationIncludeOption.Participants,
          ConversationIncludeOption.ConversationType,
          ConversationIncludeOption.ListItemTemplateData,
          ConversationIncludeOption.ClientParticipantFrontendUrl,
        ],
      });
      updateState(
        this.#conversationStore,
        'Update conversation in ConversationStore',
        updateEntity({ id: conversationId, changes: { ...response } })
      );
      updateState(
        this.#activeConversationStore,
        'Update conversation in ActiveConversationStore',
        updateEntity({ id: conversationId, changes: { ...response } })
      );
    } catch (err) {
      const errorMessage = (err as any)?.error?.message;
      if (errorMessage) {
        updateState(this.#conversationStore, 'Set error in ConversationStore', {
          error: errorMessage,
        });
        updateState(this.#activeConversationStore, 'Set error in ActiveConversationStore', {
          error: errorMessage,
        });
      } else {
        logger.error(err);
      }
    }
  }

  async unlockConversation(conversationId: string) {
    try {
      const response = await this.#wireApiService.conversationsApi.unlockConversation({
        conversationId: conversationId,
        includes: [
          ConversationIncludeOption.Participants,
          ConversationIncludeOption.ConversationType,
          ConversationIncludeOption.ListItemTemplateData,
          ConversationIncludeOption.ClientParticipantFrontendUrl,
        ],
      });
      updateState(
        this.#conversationStore,
        'Update conversation in ConversationStore',
        updateEntity({ id: conversationId, changes: { ...response } })
      );
      updateState(
        this.#activeConversationStore,
        'Update conversation in ActiveConversationStore',
        updateEntity({ id: conversationId, changes: { ...response } })
      );
    } catch (err) {
      const errorMessage = (err as any)?.error?.message;
      if (errorMessage) {
        updateState(this.#conversationStore, 'Set error in ConversationStore', {
          error: errorMessage,
        });
        updateState(this.#activeConversationStore, 'Set error in ActiveConversationStore', {
          error: errorMessage,
        });
      } else {
        logger.error(err);
      }
    }
  }

  openLocationSelectorOverlay(conversationId: string, nextMessageSourceId?: string): void {
    const positionStrategy = this.#overlay.position().global().centerHorizontally().bottom();

    const dialogRef = this.#dialog.open(LocationOverlayComponent, {
      width: '61.25rem',
      panelClass: 'location-dialog',
      positionStrategy,
    });

    dialogRef.componentInstance?.sendLocation.subscribe(async (value) => {
      await this.createNewMessage(
        conversationId,
        'LOCATION',
        nextMessageSourceId ?? uuid.v4(),
        undefined,
        {
          longitude: value.longitude,
          latitude: value.latitude,
          accuracy: value.accuracy,
          source: value.source,
          language: value.language,
        },
        value.formatted_address
      );
      dialogRef.close();
    });
  }
}
